import React, { useState, useEffect } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import { ArrowUpRight, ArrowDownRight, Plus, Layers } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const timeRanges = ["1D", "7D", "1M", "3M", "1Y", "ALL"];

// Custom 3D Donut Chart using SVG stacking (CSS 3D effect)
function Donut3D({ items }) {
  const size = 200;
  const cx = size / 2;
  const cy = size / 2;
  const outerR = 90;
  const innerR = 55;
  const depth = 20; // Number of stacked layers for 3D depth

  const colorMap = {
    'bg-indigo-500': '#6366f1',
    'bg-cyan-500': '#06b6d4',
    'bg-purple-500': '#a855f7',
    'bg-emerald-500': '#10b981',
    'bg-amber-500': '#f59e0b',
    'bg-rose-500': '#f43f5e',
  };

  let cumulative = 0;
  const slices = items.map((item) => {
    const start = cumulative;
    const pct = parseFloat(item.percentage) / 100;
    cumulative += pct;
    return { ...item, start, end: cumulative, color: colorMap[item.color] || '#6366f1' };
  });

  const polarToCartesian = (cx, cy, r, deg) => {
    const rad = ((deg - 90) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const arcPath = (cx, cy, outerR, innerR, startPct, endPct) => {
    // If it's exactly 100%, we need to draw two half-circles to avoid SVG path rendering issues
    if (endPct - startPct === 1) {
      const s1 = polarToCartesian(cx, cy, outerR, 0);
      const e1 = polarToCartesian(cx, cy, outerR, 180);
      const e2 = polarToCartesian(cx, cy, outerR, 359.9);
      const s2_inner = polarToCartesian(cx, cy, innerR, 359.9);
      const e1_inner = polarToCartesian(cx, cy, innerR, 180);
      const s1_inner = polarToCartesian(cx, cy, innerR, 0);
      return `
        M ${s1.x} ${s1.y} A ${outerR} ${outerR} 0 1 1 ${e1.x} ${e1.y} A ${outerR} ${outerR} 0 1 1 ${e2.x} ${e2.y}
        L ${s2_inner.x} ${s2_inner.y} A ${innerR} ${innerR} 0 1 0 ${e1_inner.x} ${e1_inner.y} A ${innerR} ${innerR} 0 1 0 ${s1_inner.x} ${s1_inner.y} Z
      `;
    }

    // Normal slice
    const s1 = polarToCartesian(cx, cy, outerR, startPct * 360);
    const e1 = polarToCartesian(cx, cy, outerR, endPct * 360);
    const s2 = polarToCartesian(cx, cy, innerR, endPct * 360);
    const e2 = polarToCartesian(cx, cy, innerR, startPct * 360);
    const large = endPct - startPct > 0.5 ? 1 : 0;
    return `M ${s1.x} ${s1.y} A ${outerR} ${outerR} 0 ${large} 1 ${e1.x} ${e1.y} L ${s2.x} ${s2.y} A ${innerR} ${innerR} 0 ${large} 0 ${e2.x} ${e2.y} Z`;
  };

  const topItem = items[0];

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size + 40, perspective: '1000px' }}>
      
      <div 
        className="relative w-full h-full" 
        style={{ 
          transformStyle: 'preserve-3d', 
          transform: 'rotateX(55deg)',
          marginTop: '-20px'
        }}
      >
        {/* Glow beneath */}
        <div className="absolute inset-0 bg-indigo-500/20 blur-[30px] rounded-full transform translate-y-12 scale-90" />

        {/* Extrusion layers */}
        {Array.from({ length: depth }).map((_, i) => {
          const isBottom = i === depth - 1;
          return (
            <svg 
              key={`layer-${i}`} 
              width={size} 
              height={size} 
              className="absolute top-0 left-0"
              style={{ 
                transform: `translateZ(${-i * 1.5}px)`,
                filter: `brightness(${0.3 + (1 - i/depth) * 0.4})` // Gets darker as it goes down
              }}
            >
              {slices.map((s, j) => (
                <path
                  key={`slice-${i}-${j}`}
                  d={arcPath(cx, cy, outerR, innerR, s.start, s.end)}
                  fill={s.color}
                  stroke={isBottom ? 'rgba(0,0,0,0.5)' : 'none'}
                  strokeWidth={isBottom ? 1 : 0}
                />
              ))}
            </svg>
          );
        })}

        {/* Top layer (Brightest) */}
        <svg 
          width={size} 
          height={size} 
          className="absolute top-0 left-0 drop-shadow-[0_0_15px_rgba(255,255,255,0.1)]"
          style={{ transform: 'translateZ(0px)' }}
        >
          <defs>
            {slices.map((s, i) => (
              <linearGradient key={`grad-${i}`} id={`topGrad-${i}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={s.color} stopOpacity="1" />
                <stop offset="100%" stopColor={s.color} stopOpacity="0.8" />
              </linearGradient>
            ))}
          </defs>
          {slices.map((s, i) => (
            <path
              key={`top-slice-${i}`}
              d={arcPath(cx, cy, outerR, innerR, s.start, s.end)}
              fill={`url(#topGrad-${i})`}
              stroke="rgba(255,255,255,0.15)"
              strokeWidth="1"
              className="hover:brightness-110 transition-all cursor-pointer"
            />
          ))}
        </svg>

      </div>

      {/* Center text overlay */}
      <div className="absolute flex flex-col items-center justify-center pointer-events-none" style={{ marginTop: '-10px' }}>
        <span className="text-3xl font-black text-white font-outfit tracking-tighter drop-shadow-lg">
          {topItem?.percentage || '0'}%
        </span>
        <span className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mt-1">
          {topItem?.asset || 'TOTAL'}
        </span>
      </div>
    </div>
  );
}

// Mini Sparkline for asset cards
function Sparkline({ data, color }) {
  return (
    <ResponsiveContainer width="100%" height={40}>
      <LineChart data={data}>
        <defs>
          <linearGradient id={`spark-${color}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="1" />
          </linearGradient>
        </defs>
        <Line
          type="monotone"
          dataKey="v"
          stroke={`url(#spark-${color})`}
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// Custom tooltip for main chart
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900/95 border border-indigo-500/30 rounded-xl px-4 py-3 shadow-2xl backdrop-blur-xl">
        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">{label}</p>
        <p className="text-lg font-black text-white font-outfit">
          ${payload[0]?.value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
      </div>
    );
  }
  return null;
};

export default function PortfolioChart({ wallets = [], totalBalance = 0 }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedRange, setSelectedRange] = useState("1M");

  const getPortfolioDistribution = () => {
    if (!wallets.length) return [];
    const combinedAssets = {};
    wallets.forEach(w => {
      const symbol = w.symbol || w.asset;
      if (!combinedAssets[symbol]) {
        combinedAssets[symbol] = { symbol, name: w.name || symbol, balance: 0, value_usdt: 0 };
      }
      combinedAssets[symbol].balance += parseFloat(w.balance) || 0;
      combinedAssets[symbol].value_usdt += parseFloat(w.value_usdt) || 0;
    });
    const assetsArray = Object.values(combinedAssets).filter(a => a.value_usdt > 0.01);
    const total = assetsArray.reduce((sum, a) => sum + a.value_usdt, 0);
    const colors = ['bg-indigo-500', 'bg-cyan-500', 'bg-purple-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500'];
    const hexColors = ['#6366f1', '#06b6d4', '#a855f7', '#10b981', '#f59e0b', '#f43f5e'];
    return assetsArray.map((a, index) => ({
      asset: a.symbol,
      amount: a.balance.toFixed(4),
      value: a.value_usdt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      valueNum: a.value_usdt,
      percentage: total > 0 ? ((a.value_usdt / total) * 100).toFixed(1) : '0',
      color: colors[index % colors.length],
      hexColor: hexColors[index % hexColors.length],
    })).sort((a, b) => parseFloat(b.percentage) - parseFloat(a.percentage));
  };

  const portfolioItems = getPortfolioDistribution();

  // Generate synthetic performance data based on totalBalance
  const generatePerformanceData = (range) => {
    const base = parseFloat(totalBalance) || 1000;
    const points = { '1D': 24, '7D': 7, '1M': 30, '3M': 90, '1Y': 12, 'ALL': 24 }[range] || 30;
    const labels1M = ['May 11', 'May 18', 'May 25', 'Jun 1', 'Jun 8'];
    const labels = range === '1M' ? labels1M : Array.from({ length: points }, (_, i) => `P${i + 1}`);
    let current = base * 0.65;
    return labels.map((label, i) => {
      const growth = 1 + (Math.random() * 0.08 - 0.02);
      current = i === labels.length - 1 ? base : current * growth;
      return { label, value: Math.round(current * 100) / 100 };
    });
  };

  const perfData = generatePerformanceData(selectedRange);

  // Sparkline data for asset cards
  const genSparkline = (trend = 'up') => {
    let v = 50;
    return Array.from({ length: 12 }, () => {
      v += trend === 'up' ? Math.random() * 6 - 1 : Math.random() * 2 - 4;
      return { v: Math.max(10, v) };
    });
  };

  const cryptoIcons = {
    BTC: { color: '#f97316', bg: 'bg-orange-500', letter: '₿' },
    USDT: { color: '#10b981', bg: 'bg-emerald-500', letter: '₮' },
    ETH: { color: '#8b5cf6', bg: 'bg-violet-500', letter: 'Ξ' },
    BNB: { color: '#f59e0b', bg: 'bg-amber-500', letter: 'B' },
  };

  return (
    <div className="flex flex-col gap-6">

      {/* ═══ MAIN PERFORMANCE AREA ═══ */}
      <div className="relative rounded-2xl overflow-hidden border border-white/8 bg-gradient-to-br from-[#0d1629] via-[#0f1a2e] to-[#0a1020] p-6 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
        
        {/* Glow background */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600/10 rounded-full blur-[80px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-500/8 rounded-full blur-[60px] pointer-events-none" />

        {/* Top row: Total Value + Time selector + 3D image */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-2 relative z-10">
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Total Value</p>
            <h2 className="text-3xl sm:text-4xl font-black text-white font-outfit tracking-tighter leading-none break-all">
              ${parseFloat(totalBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </h2>
            <div className="flex items-center gap-3 mt-3">
              <div className="flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-500/25 px-3 py-1 rounded-lg">
                <ArrowUpRight size={12} className="text-emerald-400" />
                <span className="text-emerald-400 font-black text-xs">12.4%</span>
              </div>
              <span className="text-slate-500 text-xs font-bold">vs last month</span>
            </div>
          </div>

          {/* Time Range selector only */}
          <div className="flex items-center gap-1 bg-slate-900/60 border border-white/8 rounded-xl p-1">
            {timeRanges.map(r => (
              <button
                key={r}
                onClick={() => setSelectedRange(r)}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-black tracking-widest transition-all ${
                  selectedRange === r
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/40'
                    : 'text-slate-500 hover:text-white hover:bg-white/5'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {/* Performance Chart */}
        <div className="relative z-10 h-48 -mx-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={perfData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="perfGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity="0.35" />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity="0" />
                </linearGradient>
                <filter id="lineGlow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                  <feMerge>
                    <feMergeNode in="coloredBlur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 10, fontWeight: 700 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotoneX"
                dataKey="value"
                stroke="#818cf8"
                strokeWidth={2.5}
                fill="url(#perfGrad)"
                dot={false}
                activeDot={{ r: 6, fill: '#6366f1', stroke: '#fff', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Last value badge */}
        <div className="flex justify-end -mt-2 relative z-10">
          <div className="bg-indigo-600 text-white text-[10px] font-black px-3 py-1 rounded-lg shadow-lg shadow-indigo-500/40">
            ${parseFloat(totalBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {/* ═══ BOTTOM GRID ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Portfolio Allocation */}
        <div className="relative rounded-2xl overflow-hidden border border-white/8 bg-gradient-to-br from-[#0d1629] via-[#0f1a2e] to-[#0a1020] p-6 shadow-[0_16px_40px_rgba(0,0,0,0.5)]">
          <div className="absolute top-0 right-0 w-32 h-32 bg-purple-600/8 rounded-full blur-[60px] pointer-events-none" />
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm font-black text-white uppercase tracking-wider">Portfolio Allocation</h3>
            <button className="text-slate-500 hover:text-white transition-colors">
              <span className="text-lg font-black">···</span>
            </button>
          </div>

          <div className="flex items-center gap-6">
            {portfolioItems.length > 0 ? (
              <Donut3D items={portfolioItems} />
            ) : (
              <div className="w-[200px] h-[200px] flex items-center justify-center">
                <div className="w-32 h-32 rounded-full border-[12px] border-slate-800 flex items-center justify-center">
                  <span className="text-slate-600 text-xs font-black uppercase">Empty</span>
                </div>
              </div>
            )}

            <div className="flex-1 space-y-4">
              {portfolioItems.length > 0 ? portfolioItems.map((item, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${item.color}`} />
                      <span className="text-sm font-black text-white uppercase">{item.asset}</span>
                    </div>
                    <span className="text-sm font-black text-white font-outfit">{item.percentage}%</span>
                  </div>
                  <div className="flex items-center justify-between text-[10px] text-slate-500 font-bold uppercase">
                    <span>{item.amount} {item.asset}</span>
                    <span>${item.value}</span>
                  </div>
                  <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full ${item.color} rounded-full opacity-70`} style={{ width: `${item.percentage}%` }} />
                  </div>
                </div>
              )) : (
                <div className="text-center py-4">
                  <p className="text-[10px] text-slate-600 font-black uppercase tracking-widest">No assets yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Different Assets */}
          <div className="mt-6 p-4 rounded-xl bg-slate-900/60 border border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-500/15 rounded-xl">
                <Layers size={18} className="text-indigo-400" />
              </div>
              <div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Different Assets</p>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-black text-white font-outfit">{portfolioItems.length}</span>
                  <span className="text-[10px] text-slate-500 font-bold">Assets in your portfolio</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => navigate('/deposit')}
              className="p-2 bg-slate-800 hover:bg-indigo-600 rounded-xl border border-slate-700 hover:border-indigo-500 text-slate-400 hover:text-white transition-all"
            >
              <ArrowUpRight size={16} />
            </button>
          </div>
        </div>

        {/* Right column: Asset cards + Performance */}
        <div className="flex flex-col gap-4">
          {/* Asset cards */}
          <div className="grid grid-cols-2 gap-4">
            {(portfolioItems.length > 0 ? portfolioItems.slice(0, 2) : [
              { asset: 'BTC', amount: '0.00', value: '0.00', percentage: '0', hexColor: '#f97316' },
              { asset: 'USDT', amount: '0.00', value: '0.00', percentage: '0', hexColor: '#10b981' },
            ]).map((item, i) => {
              const icon = cryptoIcons[item.asset] || { color: item.hexColor, bg: 'bg-indigo-500', letter: item.asset[0] };
              const sparkColor = icon.color || item.hexColor;
              const trend = i === 0 ? 'up' : 'flat';
              const changeVal = i === 0 ? '+8.7%' : '+6.3%';
              return (
                <div key={i} className="relative rounded-2xl overflow-hidden border border-white/8 bg-gradient-to-br from-[#0d1629] via-[#0f1a2e] to-[#0a1020] p-4 shadow-[0_12px_30px_rgba(0,0,0,0.5)]">
                  <div className="absolute top-0 right-0 w-20 h-20 rounded-full blur-[40px] pointer-events-none" style={{ background: `${sparkColor}15` }} />
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-full ${icon.bg} flex items-center justify-center text-white font-black text-sm shadow-lg`}>
                        {icon.letter}
                      </div>
                      <span className="text-sm font-black text-white uppercase">{item.asset}</span>
                    </div>
                  </div>
                  <div className="mb-1">
                    <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">{item.amount} {item.asset}</p>
                    <p className="text-xl font-black text-white font-outfit">${item.value}</p>
                  </div>
                  <Sparkline data={genSparkline(trend)} color={sparkColor} />
                  <div className="flex items-center gap-1 mt-1">
                    <ArrowUpRight size={10} className="text-emerald-400" />
                    <span className="text-[10px] text-emerald-400 font-black">{changeVal}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Performance mini chart */}
          <div className="relative flex-1 rounded-2xl overflow-hidden border border-white/8 bg-gradient-to-br from-[#0d1629] via-[#0f1a2e] to-[#0a1020] p-5 shadow-[0_16px_40px_rgba(0,0,0,0.5)]">
            <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/8 rounded-full blur-[60px] pointer-events-none" />
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-black text-white uppercase tracking-wider">
                Performance <span className="text-slate-500">({selectedRange})</span>
              </h3>
              <div className="bg-indigo-600 text-white text-[10px] font-black px-2.5 py-1 rounded-lg shadow-lg shadow-indigo-500/30">
                ${parseFloat(totalBalance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={perfData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="perfGrad2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity="0.3" />
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
                  <XAxis dataKey="label" tick={{ fill: '#64748b', fontSize: 9, fontWeight: 700 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 9 }} axisLine={false} tickLine={false} width={40} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#06b6d4"
                    strokeWidth={2}
                    fill="url(#perfGrad2)"
                    dot={false}
                    activeDot={{ r: 5, fill: '#06b6d4', stroke: '#fff', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}